import { type ButtonHTMLAttributes, forwardRef, type MouseEvent, memo, useCallback } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';

import { CheckIcon } from '@air/design-icons';
import { cn, tailwindVariants } from '@air/utils-tailwind';

const workspaceAgentClarifyOption = tailwindVariants({
  base: 'flex w-full items-center gap-1 rounded-full border border-gray-5 p-2 text-left',
  defaultVariants: {
    isSelected: false,
  },
  variants: {
    isInteractive: {
      true: 'bg-gray-1 transition-colors duration-150 ease-out hover:border-gray-6 hover:bg-gray-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-altitude-8 active:border-gray-6 active:bg-gray-2',
    },
    isSelected: {
      false: 'bg-gray-1',
      true: 'bg-gray-2',
    },
  },
});

export type WorkspaceAgentClarifyHotkeyGuard = (event: KeyboardEvent) => boolean;

export type WorkspaceAgentClarifyOptionProps = {
  className?: string;
  'data-testid'?: string;
  /**
   * Off by default. Choosers next to a TipTap composer opt in; thread clarify must not steal digits
   * from other contenteditables on the page.
   */
  enableOnContentEditable?: boolean;
  /**
   * Number key that selects this option. Only bound while the option is interactive, so a read-only
   * answer does not keep stealing digits.
   */
  hotkey?: string;
  /**
   * Zero-based; rendered as a 1-based badge so options can be referred to by number. `undefined` for a
   * selection that has no position in the list, which renders no badge rather than a wrong one.
   */
  index: number | undefined;
  isSelected?: boolean;
  label: string;
  /** Stable id written to `data-option-id`. Falls back to the label when a choice is only a string. */
  optionId?: string;
  /** Receives the option id (or the label, when there is no id) so a list can share one handler. */
  onSelect?: (optionId: string) => void;
  /** When set, the digit is ignored unless this returns true. Callers gate on panel containment. */
  shouldHandleHotkey?: WorkspaceAgentClarifyHotkeyGuard;
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children' | 'onSelect'>;

/**
 * One answer to a clarifying question. Interactive while the question is open, static once it has
 * been answered — supplying `onSelect` is what decides which.
 */
export const WorkspaceAgentClarifyOption = memo(
  forwardRef<HTMLButtonElement, WorkspaceAgentClarifyOptionProps>(function WorkspaceAgentClarifyOption(
    {
      className,
      'data-testid': testId,
      enableOnContentEditable = false,
      hotkey,
      index,
      isSelected = false,
      label,
      onClick,
      onSelect,
      optionId,
      shouldHandleHotkey,
      ...restOfProps
    },
    ref,
  ) {
    const value = optionId ?? label;
    const handleSelect = useCallback(() => onSelect?.(value), [onSelect, value]);
    const handleClick = useCallback(
      (event: MouseEvent<HTMLButtonElement>) => {
        onClick?.(event);
        handleSelect();
      },
      [handleSelect, onClick],
    );

    const handleHotkey = useCallback(
      (event: KeyboardEvent) => {
        if (shouldHandleHotkey && !shouldHandleHotkey(event)) {
          return;
        }

        event.preventDefault();
        handleSelect();
      },
      [handleSelect, shouldHandleHotkey],
    );

    useHotkeys(
      hotkey ?? '1',
      handleHotkey,
      {
        enabled: Boolean(hotkey) && Boolean(onSelect),
        enableOnContentEditable,
        // Prevent only after the guard accepts. A blanket preventDefault swallows digits in other
        // contenteditables, and re-dispatching the key from here has overflowed the call stack.
        preventDefault: false,
      },
      [enableOnContentEditable, handleHotkey, hotkey, onSelect],
    );

    const content = (
      <>
        {index === undefined ? null : (
          <span className="flex size-4 shrink-0 items-center justify-center font-bold text-10 text-altitude-9 tabular-nums">
            {index + 1}
          </span>
        )}
        <span
          className={cn(
            'line-clamp-2 min-w-0 flex-1 text-12 text-gray-12 leading-4',
            isSelected ? 'font-semibold' : 'font-medium',
          )}
          title={label}
        >
          {label}
        </span>
        {isSelected ? <CheckIcon className="shrink-0 text-grass-9" size="xs" /> : null}
      </>
    );

    if (!onSelect) {
      return (
        <div
          aria-current={isSelected ? 'true' : undefined}
          // Dimming the options that were not taken is what makes the answered card readable at a
          // glance: the choice stays legible and the alternatives recede without disappearing.
          className={workspaceAgentClarifyOption({
            className: cn(className, isSelected ? undefined : 'opacity-60'),
            isSelected,
          })}
          data-option-id={optionId}
          data-testid={testId}
        >
          {content}
        </div>
      );
    }

    return (
      <button
        {...restOfProps}
        className={workspaceAgentClarifyOption({ className, isInteractive: true, isSelected })}
        data-option-id={optionId}
        data-testid={testId}
        onClick={handleClick}
        ref={ref}
        type="button"
      >
        {content}
      </button>
    );
  }),
);

WorkspaceAgentClarifyOption.displayName = 'WorkspaceAgentClarifyOption';
